import { useAnaliseLaboratorial } from "@/hooks/useAnaliseLaboratorial";
import { useEnsaiosAnalise } from "@/hooks/useEnsaiosAnalise";
import { useEstabelecimento } from "@/hooks/useEstabelecimento";
import { useLaboratorioAnalise } from "@/hooks/useLaboratorioAnalise";
import { useMarcaProduto } from "@/hooks/useMarcaProduto";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { useProdutoAutorizado } from "@/hooks/useProdutoAutorizado";
import { useProgramacaoColetaAnalise } from "@/hooks/useProgramacaoColetaAnalise";
import { useServidor } from "@/hooks/useServidor";
import { IAnaliseLaboratorial } from "@/interfaces/IAnaliseLaboratorial";
import { IFormValuesRequisicaoAnalise } from "@/interfaces/IFormValuesRequisicaoAnalise";
import { formatMessageError } from "@/utils/formatMessageError";
import {
  CloseOutlined,
  FilePdfOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  App,
  Button,
  Col,
  Divider,
  Form,
  Grid,
  Row,
  Space,
  Typography,
} from "antd";
import { saveAs } from "file-saver";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useReactToPrint } from "react-to-print";
import { ImpressaoAnaliseLaboratorial } from "../../_components/ImpressaoAnaliseLaboratorial";
import { AssinaturaRequisicao } from "../_components/AssinaturaRequisicao";
import { FormDadosGerais } from "../_components/FormDadosGerais";

export function NovaRequisicaoAnaliseLaboratorial() {
  const { isOnline } = useNetworkStatus();
  const [searchParams] = useSearchParams();
  const screens = Grid.useBreakpoint();
  const navigate = useNavigate();
  const { modal, notification, message } = App.useApp();
  const solicitacaoRef = useRef<HTMLDivElement>(null);
  const [form] = Form.useForm<IFormValuesRequisicaoAnalise>();
  const { getEstabelecimentoById } = useEstabelecimento();
  const { getProgramacaoById } = useProgramacaoColetaAnalise();
  const { getProdutoAutorizadoById } = useProdutoAutorizado();
  const { getMarcaProdutoById } = useMarcaProduto();
  const { listaEnsaios } = useEnsaiosAnalise();
  const { getServidorLogged } = useServidor();
  const { getLaboratorioById } = useLaboratorioAnalise();
  const { addRequisicao, submitRequisicaoApi } = useAnaliseLaboratorial();
  const [solicitacaoMinuta, setSolicitacaoMinuta] =
    useState<IAnaliseLaboratorial | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBack = () => {
    navigate(
      `/analises-laboratoriais/requisicoes/lista?${searchParams.toString()}`
    );
  };

  const handleCancel = () => {
    modal.confirm({
      title: "Cancelar",
      content: "Tem certeza que deseja cancelar o cadastro?",
      okText: "Sim",
      okType: "danger",
      cancelText: "Nao",
      onOk: () => {
        handleBack();
      },
    });
  };

  const handleSubmit = async (values: IFormValuesRequisicaoAnalise) => {
    const estabelecimento = getEstabelecimentoById(values.estabelecimento.id);
    const programacao = getProgramacaoById(values.programacao.id);
    const produto = getProdutoAutorizadoById(values.produto.id);
    const marca = getMarcaProdutoById(values.marca.id);
    const laboratorio = getLaboratorioById(values.laboratorio.id);
    const ensaios = listaEnsaios.filter((e) => values.ensaios.includes(e.id));
    const servidor = getServidorLogged();

    if (!estabelecimento || !servidor) {
      notification.error({
        message: "Nao foi possivel salvar a Requisição de Análise Laboratorial",
        description: "Erro ao buscar dados relacionados",
        key: "submit-requisicao-analise",
      });
      return;
    }

    const request: IAnaliseLaboratorial = {
      estabelecimento: estabelecimento!,
      programacao: programacao || null,
      produto: produto || null,
      marca: marca || null,
      agua: values.agua,
      etapaColeta: values.etapaColeta,
      dataColeta: values.dataColeta.format("YYYY-MM-DDTHH:mm:ss"),
      natureza: values.naturezaAnalise,
      condicaoAmostra: values.condicoesAmostra,
      infAdicional: values.informacoesAdicionais,
      justificativa: values.justificativaProgramacao || null,
      dataRemessa: values.dataRemessa
        ? values.dataRemessa.format("YYYY-MM-DD")
        : null,
      lacre: values.lacre || "",
      lacreCProvaEmp: values.lacreContraProvaEmpresa || "",
      lacreCProvaSise: values.lacreContraProvaSISE || "",
      lote: values.lote || "",
      tamanhoPartida: values.tamanhoPartida || "",
      temperaturaColeta: Number(values.temperaturaAmostra),
      laboratorio: laboratorio || null,
      dataValidade: values.dataValidade
        ? values.dataValidade.format("YYYY-MM-DD")
        : null,
      dataFabricacao: values.dataFabricacao
        ? values.dataFabricacao.format("YYYY-MM-DD")
        : null,
      observacaoColeta: values.observacao || "",
      statusAssinaturaFiscalizado:
        values.statusAssinatura || "ASSINADO_FISICAMENTE",
      assinaturaFiscalizado: values.assinaturaFiscalizado || "",
      assinaturaServidor: values.assinaturaServidor || "",
      cpfAssinanteFiscalizado: values.cpfAssinante?.replace(/\D/g, "") || "",
      funcaoAssinanteFiscalizado: values.funcaoAssinante || "",
      nomeAssinanteFiscalizado: values.nomeAssinante || "",
      itens: ensaios.map((ensaio) => ({
        ensaio,
        resultado: "",
        conforme: false,
        naoRealizada: false,
      })),
      responsavelColeta: {
        id: servidor.id,
        matricula: servidor.matricula,
        cpfCnpj: servidor.pessoa.cpfCnpj,
        inscricaoEstadual: servidor.pessoa.inscricaoEstadual,
        nome: servidor.pessoa.nome,
        funcao: servidor.funcao,
      },
      fiscal: values.analiseFiscal || false,
      nomeEstabelecimento: estabelecimento!.nome || "",
      nomeMarca: marca?.nome || "",
      statusAssinaturaServidor:
        values.statusAssinatura === "ASSINADO_FISICAMENTE"
          ? "ASSINADO_FISICAMENTE"
          : "ASSINADO_DIGITALMENTE",
    };

    if (!isOnline) {
      //OFFLINE
      addRequisicao(request);
      notification.success({
        message: "Requisição de Análise Laboratorial salva com sucesso",
        description:
          "A requisição foi salva localmente e ficará com status de pendente até ser sincronizada com o servidor. Armazene o arquivo de backup para evitar perda dos dados.",
      });

      saveAs(
        new Blob([JSON.stringify(request)], {
          type: "text/plain;charset=utf-8",
        }),
        "backup_requisicao_analise_laboratorial-" + Date.now() + ".txt"
      );

      handleBack();
      return;
    }

    message.loading({
      content: "Salvando requisição...",
      key: "submit-requisicao-analise",
    });
    setIsSubmitting(true);

    try {
      //ONLINE
      const result = await submitRequisicaoApi(request);
      setIsSubmitting(false);
      message.destroy("submit-requisicao-analise");

      notification.success({
        message: "Requisição de Análise Laboratorial salva com sucesso",
        description: "A requisição foi sincronizada com o servidor.",
      });

      addRequisicao({ ...request, id: result.id, codigo: result.codigo });
      handleBack();
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
      message.destroy("submit-requisicao-analise");
      const messageError = formatMessageError(error);
      notification.error({
        message: "Nao foi possivel salvar a requisição de análise",
        description: messageError,
        key: "submit-requisicao-analise",
      });
      return;
    }
  };

  const handleGeneratePreview = async () => {
    try {
      const values = await form.validateFields({
        validateOnly: true,
      });

      const estabelecimento = getEstabelecimentoById(values.estabelecimento.id);
      const programacao = getProgramacaoById(values.programacao.id);
      const produto = getProdutoAutorizadoById(values.produto.id);
      const marca = getMarcaProdutoById(values.marca.id);
      const ensaios = listaEnsaios.filter((e) => values.ensaios.includes(e.id));
      const laboratorio = getLaboratorioById(values.laboratorio.id);
      const servidor = getServidorLogged();

      if (!estabelecimento || !servidor) {
        notification.error({
          message:
            "Nao foi possivel Gerar a prévia da requisição de analise laboratorial",
          description: "Erro ao buscar dados relacionados",
          key: "preview-requisicao-analise",
        });
        return;
      }

      const request: IAnaliseLaboratorial = {
        estabelecimento: estabelecimento!,
        programacao: programacao || null,
        produto: produto || null,
        marca: marca || null,
        agua: values.agua,
        etapaColeta: values.etapaColeta,
        dataColeta: values.dataColeta.format("YYYY-MM-DDTHH:mm:ss"),
        natureza: values.naturezaAnalise,
        condicaoAmostra: values.condicoesAmostra,
        infAdicional: values.informacoesAdicionais,
        justificativa: values.justificativaProgramacao || null,
        dataRemessa: values.dataRemessa.format("YYYY-MM-DD"),
        lacre: values.lacre || "",
        lacreCProvaEmp: values.lacreContraProvaEmpresa || "",
        lacreCProvaSise: values.lacreContraProvaSISE || "",
        lote: values.lote || "",
        tamanhoPartida: values.tamanhoPartida || "",
        temperaturaColeta: Number(values.temperaturaAmostra),
        laboratorio: laboratorio || null,
        dataValidade: values.dataValidade
          ? values.dataValidade.format("YYYY-MM-DD")
          : "",
        dataFabricacao: values.dataFabricacao
          ? values.dataFabricacao.format("YYYY-MM-DD")
          : "",
        observacaoColeta: values.observacao || "",
        statusAssinaturaFiscalizado:
          values.statusAssinatura || "ASSINADO_FISICAMENTE",
        assinaturaFiscalizado: values.assinaturaFiscalizado || "",
        assinaturaServidor: values.assinaturaServidor || "",
        cpfAssinanteFiscalizado: values.cpfAssinante?.replace(/\D/g, "") || "",
        funcaoAssinanteFiscalizado: values.funcaoAssinante || "",
        nomeAssinanteFiscalizado: values.nomeAssinante || "",
        itens: ensaios.map((ensaio) => ({
          ensaio,
          resultado: "",
          conforme: false,
          naoRealizada: false,
        })),
        responsavelColeta: {
          id: servidor.id,
          matricula: servidor.matricula,
          cpfCnpj: servidor.pessoa.cpfCnpj,
          inscricaoEstadual: servidor.pessoa.inscricaoEstadual,
          nome: servidor.pessoa.nome,
          funcao: servidor.funcao,
        },
        fiscal: values.analiseFiscal || false,
        nomeEstabelecimento: estabelecimento!.nome || "",
        nomeMarca: marca?.nome || "",
        statusAssinaturaServidor:
          values.statusAssinatura === "ASSINADO_FISICAMENTE"
            ? "ASSINADO_FISICAMENTE"
            : "ASSINADO_DIGITALMENTE",
      };

      setSolicitacaoMinuta(request);
    } catch (error) {
      console.error(error);
      const errors = form.getFieldsError();
      const errorsFiltered = errors.filter((error) => error.errors.length > 0);

      if (errorsFiltered.length > 0) {
        const messageError = errorsFiltered.map((e) => {
          return e.name;
        });

        notification.error({
          message: "Verifique os campos obrigatórios",
          key: "preview-requisicao-analise",
          description: messageError.join(", "),
        });
        return;
      }

      notification.error({
        message: "Verifique os campos obrigatórios",
        key: "preview-requisicao-analise",
      });
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: solicitacaoRef,
    documentTitle: "requisicao_Analise_laboratorial_minuta",
    suppressErrors: true,
    preserveAfterPrint: true,
    onAfterPrint: () => setSolicitacaoMinuta(null),
  });

  useEffect(() => {
    if (solicitacaoMinuta) {
      handlePrint();
    }
  }, [solicitacaoMinuta, handlePrint]);

  useEffect(() => {
    document.title =
      "Inspeção Mobile - Nova Requisição de Análise Laboratorial";
  }, []);

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} align="center">
        <Typography.Title
          level={screens.xs ? 4 : 3}
          style={{ textAlign: "center" }}
        >
          Nova Requisição de Análise Laboratorial
        </Typography.Title>
      </Space>

      <Divider />

      <Form<IFormValuesRequisicaoAnalise>
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFinishFailed={(errorInfo) => {
          console.log("Failed:", errorInfo);

          notification.error({
            message: "Verifique os campos obrigatórios",
            description: errorInfo.errorFields
              .map((err) => err.name)
              .join(", "),
            key: "form-error",
            duration: 5,
          });
        }}
      >
        <FormDadosGerais form={form} />

        <Divider orientation="left">
          <Typography.Title
            level={screens.xs ? 5 : 4}
            style={{ textAlign: "center" }}
          >
            Assinaturas
          </Typography.Title>
        </Divider>

        <AssinaturaRequisicao form={form} />

        <Divider />

        <Row justify={"center"} gutter={[10, 10]}>
          <Col>
            <Button
              danger
              htmlType="button"
              ghost
              icon={<CloseOutlined />}
              onClick={handleCancel}
              style={{ width: 200 }}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
          </Col>

          <Col>
            <Button
              icon={<SaveOutlined />}
              type="primary"
              htmlType="submit"
              style={{ width: 200 }}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Salvar
            </Button>
          </Col>

          <Col>
            <Button
              icon={<FilePdfOutlined />}
              htmlType="button"
              style={{ width: 200 }}
              onClick={handleGeneratePreview}
              loading={isSubmitting}
            >
              Gerar Prévia
            </Button>
          </Col>
        </Row>
      </Form>

      <div style={{ display: "none" }}>
        <ImpressaoAnaliseLaboratorial
          marcadagua="MINUTA"
          ref={solicitacaoRef}
          item={solicitacaoMinuta}
        />
      </div>
    </>
  );
}
